import { useState } from 'react';
import { commissionStep } from '../data';

export default function CommissionStepSection() {
  const [commissionPerSide, setCommissionPerSide] = useState(3.50);
  const [isPerSide, setIsPerSide] = useState(true);
  const [lotSize, setLotSize] = useState(1.0);
  const [customMode, setCustomMode] = useState(false);

  const multiplier = isPerSide ? 2 : 1;
  const commissionCost = commissionPerSide * multiplier * lotSize;
  const label = isPerSide ? 'per side' : 'per round-turn';

  const presets = [
    { label: 'ECN Major', comm: 3.50, perSide: true, lots: 1.0 },
    { label: 'ECN Mini', comm: 3.50, perSide: true, lots: 0.1 },
    { label: 'ECN Minor', comm: 5.00, perSide: true, lots: 1.0 },
    { label: 'Standard', comm: 0, perSide: true, lots: 1.0 },
  ];

  return (
    <section id="step-2-commission" className="relative w-full bg-background-100 overflow-hidden">
      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-12 md:py-18">

        {/* --- STEP HEADER --- */}
        <div className="mb-8 md:mb-14">
          <h2 className="text-[28px] md:text-[52px] font-[750] leading-[1.02] text-foreground-950 tracking-[-0.03em] max-w-[780px]">
            {commissionStep.title}
          </h2>
        </div>

        {/* --- MAIN GRID --- */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_440px] gap-8 lg:gap-12">

          {/* ========== LEFT — Editorial ========== */}
          <div>

            {/* Formula as large editorial typography */}
            <div className="mb-8 md:mb-10">
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-foreground-400 mb-4 md:mb-5">
                The Formula
              </div>

              <div className="space-y-2 md:space-y-3">
                {/* Line 1: Commission = */}
                <div className="flex items-baseline gap-2 md:gap-3 flex-wrap">
                  <span className="text-[28px] md:text-[44px] font-[750] text-foreground-950 tracking-[-0.02em] leading-none">
                    Commission
                  </span>
                  <span className="text-[22px] md:text-[32px] font-[200] text-foreground-400 leading-none">
                    =
                  </span>
                </div>

                {/* Line 2: Rate × Multiplier × Lot Size */}
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-[26px] md:text-[40px] font-[750] text-accent-500 tracking-[-0.02em] leading-none">
                    Rate
                  </span>
                  <span className="text-[18px] md:text-[22px] font-[300] text-foreground-400 leading-none">×</span>
                  <span className="text-[26px] md:text-[40px] font-[750] text-foreground-950 tracking-[-0.02em] leading-none">
                    {multiplier}×
                  </span>
                  <span className="text-[18px] md:text-[22px] font-[300] text-foreground-400 leading-none">×</span>
                  <span className="text-[26px] md:text-[40px] font-[750] text-foreground-950 tracking-[-0.02em] leading-none">
                    Lot Size
                  </span>
                </div>
              </div>

              {/* Tagline below formula */}
              <p className="text-[13px] md:text-[14px] text-foreground-500 mt-4 md:mt-5 max-w-[560px] leading-relaxed">
                {commissionStep.tagline}
              </p>
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-foreground-200/60 mb-8 md:mb-10" />

            {/* Breakdown — terminal data rows */}
            <div className="mb-8 md:mb-10 bg-foreground-950 rounded-2xl overflow-hidden">
              {/* Terminal header bar */}
              <div className="flex items-center gap-2 px-4 md:px-5 py-3 border-b border-foreground-800/40">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-foreground-600" />
                  <div className="w-2.5 h-2.5 rounded-full bg-foreground-600" />
                  <div className="w-2.5 h-2.5 rounded-full bg-foreground-600" />
                </div>
                <span className="text-[11px] font-[600] text-foreground-600 uppercase tracking-[0.08em] ml-2">formula — variables</span>
              </div>

              {/* Data rows — terminal output style */}
              <div className="divide-y divide-foreground-800/30">
                {commissionStep.breakdown.map((item, i) => {
                  const accentColors = [
                    'text-accent-400 border-accent-500/30',
                    'text-foreground-200 border-foreground-600/30',
                    'text-foreground-200 border-foreground-600/30',
                  ];
                  return (
                    <div key={item.symbol} className="flex items-start gap-3 md:gap-4 px-4 md:px-5 py-4 md:py-5 group">
                      {/* Left — variable name, monospace */}
                      <div className="w-[80px] sm:w-[130px] flex-shrink-0 pt-0.5">
                        <div className="text-[10px] md:text-[11px] font-[600] text-foreground-500 uppercase tracking-[0.06em] mb-1.5">
                          var {String(i + 1).padStart(2, '0')}
                        </div>
                        <code className={`text-[13px] sm:text-[16px] font-[700] font-mono leading-snug ${accentColors[i]}`}>
                          {item.symbol.replace(/([A-Z])/g, '\u200B$1')}
                        </code>
                      </div>

                      {/* Right — structured description */}
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] md:text-[14px] font-[600] text-foreground-100 mb-1">
                          {item.label}
                        </div>
                        <p className="text-[13px] md:text-[14px] leading-relaxed text-foreground-400 max-w-[520px]">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Insight */}
            <div className="border-l-2 border-accent-500/40 pl-5 md:pl-6 py-0.5">
              <div className="text-[11px] font-[700] uppercase tracking-[0.15em] text-accent-500/60 mb-3">
                Key Insight
              </div>
              <p className="text-[14px] md:text-[15px] leading-relaxed text-foreground-700 max-w-[560px]">
                {commissionStep.insight}
              </p>
            </div>
          </div>

          {/* ========== RIGHT — Dashboard Calculator ========== */}
          <div className="relative">
            {/* Dashboard panel */}
            <div className="lg:sticky lg:top-24 bg-foreground-950 rounded-2xl overflow-hidden">

              {/* Panel header bar */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-foreground-800/40">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-foreground-600" />
                    <div className="w-2 h-2 rounded-full bg-foreground-600" />
                    <div className="w-2 h-2 rounded-full bg-foreground-600" />
                  </div>
                  <span className="text-[12px] font-[600] text-foreground-400 uppercase tracking-[0.08em] ml-2.5">
                    Live Commission Calc
                  </span>
                </div>
                <span className="text-[10px] text-foreground-600 font-mono">v2.0</span>
              </div>

              {/* Panel body */}
              <div className="p-5 md:p-6 space-y-6">

                {/* Presets */}
                {!customMode && (
                  <div className="grid grid-cols-2 gap-1.5">
                    {presets.map((p, i) => {
                      const isActive = commissionPerSide === p.comm && isPerSide === p.perSide && lotSize === p.lots;
                      return (
                        <button
                          key={i}
                          onClick={() => { setCommissionPerSide(p.comm); setIsPerSide(p.perSide); setLotSize(p.lots); }}
                          className={`text-left px-3.5 py-2.5 rounded-lg border transition-all duration-200 cursor-pointer ${
                            isActive
                              ? 'border-accent-500/50 bg-accent-500/8 ring-1 ring-accent-500/20'
                              : 'border-foreground-800/30 bg-foreground-900/30 hover:border-foreground-700/40 hover:bg-foreground-900/50'
                          }`}
                        >
                          <div className={`text-[13px] font-[600] leading-snug whitespace-nowrap ${isActive ? 'text-accent-400' : 'text-foreground-200'}`}>
                            {p.label}
                          </div>
                          <div className={`text-[11px] mt-1 font-mono ${isActive ? 'text-accent-500/50' : 'text-foreground-600'}`}>
                            ${p.comm.toFixed(2)}/s · {p.lots}L
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Mode toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-foreground-500 font-mono uppercase tracking-[0.05em]">
                    {customMode ? '· custom' : '· preset'}
                  </span>
                  <button
                    onClick={() => setCustomMode(!customMode)}
                    className="text-[11px] font-[600] text-accent-400 hover:text-accent-300 transition-colors cursor-pointer tracking-[0.03em]"
                  >
                    {customMode ? '← Presets' : 'Custom →'}
                  </button>
                </div>

                {/* Separator */}
                <div className="w-full h-px bg-foreground-800/30" />

                {/* Sliders */}
                <div className="space-y-5">
                  {/* Commission Rate slider */}
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <span className="text-[11px] font-[700] text-foreground-300 uppercase tracking-[0.08em]">Rate</span>
                      <div className="flex items-baseline gap-1">
                        <span className="text-[20px] font-[750] text-accent-400 leading-none font-mono tracking-tight">
                          ${commissionPerSide.toFixed(2)}
                        </span>
                        <span className="text-[11px] text-foreground-500 font-mono">/side</span>
                      </div>
                    </div>
                    <div className="relative h-[22px] flex items-center">
                      {/* Gauge background */}
                      <div className="absolute inset-x-0 h-1 rounded-full bg-foreground-800/50 overflow-hidden pointer-events-none">
                        <div
                          className="h-full rounded-full bg-accent-500 transition-all duration-200"
                          style={{ width: `${(commissionPerSide / 15) * 100}%` }}
                        />
                      </div>
                      {/* Range input overlaid */}
                      <input
                        type="range" min="0" max="15" step="0.5" value={commissionPerSide}
                        onChange={(e) => setCommissionPerSide(parseFloat(e.target.value))}
                        className="absolute inset-0 w-full h-full appearance-none cursor-pointer bg-transparent z-10
                          [&::-webkit-slider-runnable-track]:bg-transparent
                          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[16px] [&::-webkit-slider-thumb]:h-[16px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-accent-500 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-[0_0_0_4px_rgba(255,255,255,0.04)] [&::-webkit-slider-thumb]:transition-shadow [&::-webkit-slider-thumb]:hover:shadow-[0_0_0_8px_rgba(255,255,255,0.08)]
                          [&::-moz-range-track]:bg-transparent [&::-moz-range-track]:h-1
                          [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-[16px] [&::-moz-range-thumb]:h-[16px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-accent-500 [&::-moz-range-thumb]:cursor-pointer"
                      />
                    </div>
                    <div className="flex justify-between -mt-0.5">
                      <span className="text-[10px] text-foreground-600 font-mono">$0</span>
                      <span className="text-[10px] text-foreground-600 font-mono">$15</span>
                    </div>
                  </div>

                  {/* Pricing Model row */}
                  <div className="flex items-center justify-between px-3 py-3 rounded-lg bg-foreground-900/40 border border-foreground-800/30">
                    <div>
                      <div className="text-[11px] font-[700] text-foreground-300 uppercase tracking-[0.08em]">Model</div>
                      <div className="text-[11px] text-foreground-500 font-mono mt-0.5">
                        {isPerSide ? 'per side (×2)' : 'round-turn (×1)'}
                      </div>
                    </div>
                    <button
                      onClick={() => setIsPerSide(!isPerSide)}
                      className={`relative w-14 h-7 rounded-full transition-colors cursor-pointer ${
                        isPerSide ? 'bg-accent-500' : 'bg-foreground-700'
                      }`}
                    >
                      <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full transition-all flex items-center justify-center text-[10px] font-[800] font-mono ${
                        isPerSide ? 'left-[30px] text-accent-600' : 'left-0.5 text-foreground-500'
                      }`}>
                        {isPerSide ? '×2' : '×1'}
                      </span>
                    </button>
                  </div>

                  {/* Lot Size slider */}
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <span className="text-[11px] font-[700] text-foreground-300 uppercase tracking-[0.08em]">Lot Size</span>
                      <div className="flex items-baseline gap-1">
                        <span className="text-[20px] font-[750] text-foreground-100 leading-none font-mono tracking-tight">
                          {lotSize.toFixed(2)}
                        </span>
                        <span className="text-[11px] text-foreground-500 font-mono">lots</span>
                      </div>
                    </div>
                    <div className="relative h-[22px] flex items-center">
                      <div className="absolute inset-x-0 h-1 rounded-full bg-foreground-800/50 overflow-hidden pointer-events-none">
                        <div
                          className="h-full rounded-full bg-foreground-500 transition-all duration-200"
                          style={{ width: `${(lotSize / 5.0) * 100}%` }}
                        />
                      </div>
                      <input
                        type="range" min="0.01" max="5.0" step="0.01" value={lotSize}
                        onChange={(e) => setLotSize(parseFloat(e.target.value))}
                        className="absolute inset-0 w-full h-full appearance-none cursor-pointer bg-transparent z-10
                          [&::-webkit-slider-runnable-track]:bg-transparent
                          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[16px] [&::-webkit-slider-thumb]:h-[16px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-foreground-400 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-[0_0_0_4px_rgba(255,255,255,0.04)] [&::-webkit-slider-thumb]:transition-shadow [&::-webkit-slider-thumb]:hover:shadow-[0_0_0_8px_rgba(255,255,255,0.08)]
                          [&::-moz-range-track]:bg-transparent [&::-moz-range-track]:h-1
                          [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-[16px] [&::-moz-range-thumb]:h-[16px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-foreground-400 [&::-moz-range-thumb]:cursor-pointer"
                      />
                    </div>
                    <div className="flex justify-between -mt-0.5">
                      <span className="text-[10px] text-foreground-600 font-mono">0.01</span>
                      <span className="text-[10px] text-foreground-600 font-mono">5.0</span>
                    </div>
                  </div>
                </div>

                {/* Separator */}
                <div className="w-full h-px bg-foreground-800/30" />

                {/* Result — terminal readout */}
                <div className="relative overflow-hidden rounded-xl bg-accent-500 p-5">
                  <div
                    className="absolute inset-0 opacity-[0.05]"
                    style={{
                      backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.4) 2px, rgba(255,255,255,0.4) 4px)',
                    }}
                  />
                  <div className="relative">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-[700] text-white/50 uppercase tracking-[0.1em]">Commission</span>
                      <span className="text-[10px] font-mono text-white/40">OUTPUT</span>
                    </div>
                    <div className="text-[46px] md:text-[50px] font-[800] text-white leading-none tracking-[-0.035em] font-mono">
                      ${commissionCost.toFixed(2)}
                    </div>
                    <div className="flex items-center gap-1 mt-2.5 text-[11px] text-white/50 font-mono tracking-tight">
                      <span>${commissionPerSide.toFixed(2)}/s</span>
                      <span className="text-white/25">·</span>
                      <span>{multiplier}×</span>
                      <span className="text-white/25">·</span>
                      <span>{lotSize.toFixed(2)}L</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}